// Cached cooking voicelines — the canonical phrase catalog.
//
// Each entry is a short interjection one of the cuisine-accent voices
// says at a recurring trigger point (timer started, timer warning,
// timer done, step advance, etc.). Generated once per voice via the
// generate-voiceline edge function and cached forever in Supabase
// Storage at /recipe-audio/voicelines/<voiceId>/<phraseKey>.mp3.
//
// Why cache: every cook session triggers the same handful of phrases.
// Generating per-cook would burn ElevenLabs spend on speech the user
// has already heard a hundred times. One bake-once-per-voice job
// produces a small library that every user hears with zero per-play
// cost.
//
// Adding a new phrase: append a key here, redeploy the edge function
// (which loads this catalog server-side too — see the edge fn for the
// mirrored copy), then run the bulk-generate flow per voice. Each
// (voice × phraseKey) pair becomes one MP3 in Storage.
//
// Phrase shape:
//   key       — short snake_case identifier used in Storage paths
//   default   — fallback text for any voice without an override
//   perVoice  — optional map of voice slug → cuisine-flavored variant
//
// Per-voice variants give each cuisine accent its own phrasing so the
// Italian narrator doesn't read the same English sentence the American
// narrator does. The slug key matches CONFIGURED_VOICES[].slug.
//
// All texts should be < 200 chars, sound natural read aloud, and
// NOT include recipe-specific data (ingredient names, exact timers
// — those live in per-recipe narration).
//
// To add a variant: drop a `perVoice: { italian: "…" }` entry under
// the phrase. Re-tap "BAKE" on that voice (with Force re-bake on if
// the voice was already baked with the previous text) to overwrite
// just that voice's cached MP3.

export const VOICELINES = {
  timer_started: {
    default: "Timer is started! I'll give you a warning when it's about to go off.",
    perVoice: {
      italian:  "Timer è partito! I'll let you know when she's almost ready, eh?",
      french:   "The timer is on. I will tell you when it's nearly time, oui?",
      mexican:  "¡Tiempo, amigo! I'll let you know when it's almost ready.",
      asian:    "Timer set. I'll give you a heads-up just before it's done.",
      mediterranean: "Timer's running, friend. I'll call to you before it sings.",
      dutch:    "Timer is loopt. I'll give you a shout before he goes off.",
      danish:   "Timer is going. I'll tell you when it's nearly done.",
    },
  },
  timer_warning: {
    // Duration-agnostic so the same MP3 fires whether the threshold
    // landed at 30 seconds (short sauté) or 30 minutes (overnight
    // braise). Speaking the actual remaining time would require
    // either per-(voice × duration) bakes or runtime TTS — neither
    // worth the cost when "getting close" reads naturally at any
    // remaining duration.
    default: "Heads up — you're getting close.",
    perVoice: {
      italian:  "Eh, attenzione — almost there.",
      french:   "Attention — nearly there.",
      mexican:  "Ojo — getting close, amigo.",
      asian:    "Heads up — almost there.",
      mediterranean: "Almost there, friend — keep an eye on it.",
      dutch:    "Let op — getting close.",
      danish:   "Almost done — keep an eye on it.",
    },
  },
  timer_done: {
    default: "Time's up! Come check on it.",
    perVoice: {
      italian:  "Ecco! It is ready — come check on it.",
      french:   "Voilà! Time's up — come and see.",
      mexican:  "¡Listo! Time's up — come check on it.",
      asian:    "Time's up! Come and check.",
      mediterranean: "It's ready, my friend! Come and see.",
      dutch:    "Klaar! Time's up — come have a look.",
      danish:   "Færdig! Time's up — come check on it.",
    },
  },
  step_advanced: {
    default: "Nicely done. Let's move on to the next step.",
    perVoice: {
      italian:  "Bravissimo. Now, on to the next step.",
      french:   "Très bien. Onto the next step.",
      mexican:  "¡Bien hecho! On to the next step.",
      asian:    "Nicely done. On to the next step.",
      mediterranean: "Beautiful. Now the next step.",
      dutch:    "Goed gedaan. Onto the next step.",
      danish:   "Godt gået. On to the next step.",
    },
  },
  cook_started: {
    default: "Alright, let's get cooking. Take your time and enjoy it.",
    perVoice: {
      italian:  "Allora, let's cook. Take your time, enjoy yourself.",
      french:   "Bon, let's begin. Take your time and enjoy.",
      mexican:  "Bueno, let's get cooking. Take your time, enjoy.",
      asian:    "Alright, let's cook. Take your time and enjoy.",
      mediterranean: "Come, let's cook together. Take your time.",
      dutch:    "Goed, laten we koken. Take your time and enjoy.",
      danish:   "Okay, lad os lave mad. Take your time and enjoy.",
    },
  },
  cook_finished: {
    default: "And that's the dish. Beautiful work.",
    perVoice: {
      italian:  "Ecco, the dish. Bellissimo. Beautiful work.",
      french:   "Voilà, the dish. Magnifique. Beautiful work.",
      mexican:  "Y eso es. ¡Buen trabajo!",
      asian:    "And that's the dish. Beautiful work.",
      mediterranean: "Ecco, the dish is done. Eat well, friend.",
      dutch:    "En dat is de dish. Beautiful work.",
      danish:   "Og dér er retten. Beautiful work.",
    },
  },
};

// Stable list of phrase keys. Used by the bulk-generate flow to
// iterate every (voice × phrase) pair. Adding a key above
// auto-includes it here.
export const VOICELINE_KEYS = Object.keys(VOICELINES);

// All ElevenLabs voice IDs the app uses. Mirrored from the
// ELEVENLABS_VOICE_MAP_JSON server secret because the bulk-generate
// admin UI needs to enumerate voices client-side. Keep in sync if
// you add/remove voices in the secret. These IDs are public
// identifiers (not secrets) — safe to commit to client code.
//
// `slug` is the stable lowercase key used to look up per-voice
// phrase variants in the VOICELINES catalog above. Match it exactly
// to the keys you use in `perVoice` blocks.
export const CONFIGURED_VOICES = [
  { slug: "italian",       label: "Italian",          voiceId: "lKHdfKEACEvTVM4t1I8u" },
  { slug: "french",        label: "French",           voiceId: "dsOmxSik52Mpu79xlg29" },
  { slug: "mexican",       label: "Mexican",          voiceId: "H3kQVx1IvV3HdmQ35mjy" },
  { slug: "american",      label: "American",         voiceId: "3wkFGJ6TAEXjlqoDWTya" },
  { slug: "asian",         label: "Asian (jp/th/cn)", voiceId: "j76okmt8Mm675mwshnrS" },
  { slug: "mediterranean", label: "Mediterranean",    voiceId: "RnpZVNp9QywEqEwWjCZw" },
  { slug: "dutch",         label: "Dutch",            voiceId: "XWRBvOpYYmvrezLWZC7d" },
  { slug: "danish",        label: "Danish",           voiceId: "Dzs1lWyoNDQ2t6eTa9gC" },
  { slug: "default",       label: "Default",          voiceId: "5OmaK1FZsTx0Oi0fjeNd" },
];

// Resolve the text a given voice should say for a given phrase key.
// Walks the cascade: per-voice override → catalog default → null.
// Used by the client (for telemetry / debugging) and mirrored on the
// edge function side.
export function resolveVoicelineText(phraseKey, voiceSlug) {
  const phrase = VOICELINES[phraseKey];
  if (!phrase) return null;
  if (voiceSlug && phrase.perVoice?.[voiceSlug]) return phrase.perVoice[voiceSlug];
  return phrase.default || null;
}

// Per-bake cache-bust key: when an admin bakes a voiceline, the
// admin panel writes the bake timestamp into localStorage under
// `voiceline_v:<voiceId>:<phraseKey>`. We read it back here and
// append as a `?v=<ts>` query param so the just-baked file plays
// instantly on the same device — the upload's short cacheControl
// (60s) handles cross-device propagation.
function readBakeStamp(voiceId, phraseKey) {
  try {
    if (typeof localStorage === "undefined") return null;
    const v = localStorage.getItem(`voiceline_v:${voiceId}:${phraseKey}`);
    return v && v.length ? v : null;
  } catch { return null; }
}

// Construct the public URL for a cached voiceline. Returns null when
// either the supabase client isn't ready or one of the inputs is
// missing — caller should treat null as "skip the voiceline" (e.g.
// recipe has no narration generated, so we don't know which voice
// to play). Bucket is public-read per migration 0166.
//
// Cache-bust strategy — defense in depth, layered fallbacks:
//   1. Per-bake localStorage stamp (admin who just baked, same
//      device, same browser) → instant playback of new audio.
//   2. Minute-precision auto-stamp → URL rotates every minute, so
//      browser/CDN can't pin a stale response for longer than
//      ~60s regardless of what the server's cache-control header
//      claims. Cost: a fresh fetch up to once-per-minute per
//      voiceline, but voicelines are tiny (~30-50KB) so the
//      egress is negligible.
// The two combined guarantee re-bakes propagate within 60s on every
// device, no matter what state the CDN/browser caches are in.
export function voicelineUrl(supabase, voiceId, phraseKey) {
  if (!supabase || !voiceId || !phraseKey) return null;
  if (!VOICELINES[phraseKey]) return null;
  const path = `voicelines/${voiceId}/${phraseKey}.mp3`;
  const { data } = supabase.storage.from("recipe-audio").getPublicUrl(path);
  if (!data?.publicUrl) return null;
  const bakeStamp = readBakeStamp(voiceId, phraseKey);
  const stamp = bakeStamp || String(Math.floor(Date.now() / 60000)); // minute precision
  return `${data.publicUrl}?v=${stamp}`;
}
